import {ApolloProvider} from '@apollo/client'
import loadable from '@loadable/component'
import React, {useCallback, useEffect, useLayoutEffect, useState} from 'react'
import {Routes, useNavigate} from 'react-router-dom'
import {AtomicKBar, KBarProviderWithFrecency} from './cmdk/command-palette'
import {OrganisationPreferences, RoutingOrganisation} from './common/types'
import {NotFound} from './components/errors'
import {Header} from './components/header'
import {Layout} from './components/layout'
import {GlobalStyle} from './components/library/theme'
import {Loader} from './components/loader'
import {resetActivity, SessionTimeout} from './components/session-timeout'
import {Sidebar} from './components/sidebar'
import {EnvironmentProvider, useEnvironment} from './environment'
import {useAnalytics} from './lib/analytics/analytics'
import {getClient} from './lib/api'
import {AtomicSingleCard, AtomicStream} from './lib/atomic'
import {useAuth} from './lib/auth'
import {AuthState} from './lib/cognito'
import {ENVIRONMENT_NAME} from './lib/env'
import {ErrorBoundary} from './lib/error-boundary'
import {useOrganisations} from './lib/organisations'
import {PrivateRoute, Route} from './lib/router'
import {ToastProvider} from './lib/toast'
import {
  Organisation,
  OrganisationData,
  OrganisationRedirect,
  useOrganisation
} from './organisation'
import {CrumbProvider} from './providers/crumbs'
import {AtomicSDKProvider, useAtomicSDKContext} from './providers/atomic-sdk'
import {atomicConfig} from './hooks/atomic-sdk'
import {FrecencyProvider} from './providers/frecency'
import {UpdatePromptToast} from './update-prompt'
import {MfaSetup} from './views/profile/mfa'

const DEFAULT_INACTIVITY_TIMEOUT = 1800

const DemoApp = loadable(async () => (await import('./demo-login/app')).App, {
  fallback: <Loader />
})

const ConfirmAccount = loadable(
  async () => (await import('./views/auth/confirm')).ConfirmAccount,
  {
    fallback: <Loader />
  }
)

const HandleAuthCode = loadable(
  async () => (await import('./views/auth/code')).HandleAuthCode,
  {
    fallback: <Loader />
  }
)

const HandleSSOLogin = loadable(
  async () => (await import('./views/auth/sso')).HandleSSOLogin,
  {
    fallback: <Loader />
  }
)

const AcceptInvitation = loadable(
  async () => (await import('./views/auth')).AcceptInvitation,
  {
    fallback: <Loader />
  }
)

const OrganisationProviderRoute = ({
  id,
  setOrganisationData,
  authState,
  signOut,
  organisations
}: {
  id: string
  setOrganisationData: (orgData: OrganisationData) => void
  authState: AuthState
  signOut: () => Promise<void>
  organisations: RoutingOrganisation[]
}) => {
  const {atomicSDK} = useAtomicSDKContext()
  // since we have a definite id here, capture org data, and pass it to the
  // parent component via callback
  const orgData = useOrganisation(id)
  const {environment} = useEnvironment()

  useLayoutEffect(() => {
    setOrganisationData(orgData)
  }, [orgData, setOrganisationData])

  // TODO: Investigate possible stale organizationId in apollo client
  // When switching between environments from different organizations (when not reloading the page), the Apollo client
  // may use a stale organizationId to make requests from time to time.
  // This issue specifically affects useGetOrganizationAccessLazyQuery in useOrganization.
  // Resulting in NotFound Page.
  // For example, when switching from org '01' to org '02', the Apollo client in useGetOrganizationAccessLazyQuery
  // may still make requests to '01.customer.api.atomic.io' to get the '02' organization access.
  // Refer to PR https://github.com/atomic-app/action-cards/pull/9381/files for more details.
  return authState.authenticated ? (
    <ApolloProvider client={getClient(id)}>
      <UpdatePromptToast />
      <AtomicKBar
        organisations={organisations}
        organisationId={orgData.organisationId}
        environment={environment}
        signOut={signOut}
      />
      <Organisation
        authState={authState}
        organisationData={orgData}
        atomicSDK={atomicSDK}
      />
    </ApolloProvider>
  ) : (
    <Loader />
  )
}

export const App = () => {
  const {auth, authState} = useAuth()
  const analytics = useAnalytics()
  const navigate = useNavigate()
  const [organisations] = useOrganisations()
  const [signingOut, setSigningOut] = useState(false)
  const [organisationData, setOrganisationData] = useState<
    OrganisationData | undefined
  >()
  const {currentBusinessUser} = organisationData?.organisation ?? {}

  const signOut = useCallback(async () => {
    setSigningOut(true)
    await analytics.track(
      {
        event: 'Logged out of workbench',
        properties: {
          category: 'general',
          //@ts-ignore in reality this will be set, and if not sending undefined is ok
          org_id: organisationData?.organisationId
        }
      },
      true
    )
    auth.signOut()
  }, [auth, analytics, organisationData?.organisationId])

  const showHeader = true

  useEffect(() => {
    // ensure we reset activity whenever viewing this screen in a logged out
    // state, so that when the user does log in they don't get logged out
    // immediately
    auth.on('stateChange', ({authenticated}) => {
      if (!authenticated) {
        resetActivity()
      }
    })
  }, [auth])

  const inactivityTimeout = organisationData
    ? getInactivityTimeout(
        organisationData.organisationId,
        organisationData.organisation?.preferences
      )
    : undefined

  return signingOut ? (
    <Loader />
  ) : (
    <FrecencyProvider>
      <KBarProviderWithFrecency>
        <CrumbProvider>
          <AtomicSDKProvider>
            <GlobalStyle />
            <EnvironmentProvider
              organisation={organisationData?.organisation}
              render={({environment, switcherMode}) => (
                <Layout
                  authenticated={authState.authenticated}
                  singleCard={
                    <AtomicSingleCard
                      organisation={organisationData?.organisation}
                      environment={environment}
                      currentBusinessUser={currentBusinessUser}
                      streamContainerId={
                        atomicConfig.ATOMIC_HIGH_PRIORITY_CONTAINER_ID
                      }
                    />
                  }
                  stream={
                    <>
                      <AtomicStream
                        organisation={organisationData?.organisation}
                        environment={environment}
                        currentBusinessUser={currentBusinessUser}
                        streamContainerId={atomicConfig.ATOMIC_CONTAINER_ID}
                      />
                    </>
                  }
                  sidebar={
                    authState.authenticated ? (
                      <Sidebar
                        environment={environment}
                        authState={authState}
                        signOut={signOut}
                        organisation={organisationData?.organisation}
                        testUserId={currentBusinessUser?.linkedTestUserId}
                      />
                    ) : undefined
                  }
                  header={
                    authState.authenticated && showHeader ? (
                      <Header
                        environment={environment}
                        authState={authState}
                        organisations={organisations}
                        switcherMode={switcherMode}
                      />
                    ) : null
                  }
                >
                  <ErrorBoundary>
                    <ToastProvider>
                      {!authState.loaded ? (
                        <Loader />
                      ) : (
                        <Routes>
                          <PrivateRoute
                            path="/"
                            render={() => <OrganisationRedirect />}
                          />
                          <PrivateRoute
                            path="/cards"
                            render={() => (
                              <OrganisationRedirect subPath={'cards'} />
                            )}
                          />
                          <PrivateRoute
                            path="/action-flows"
                            render={() => (
                              <OrganisationRedirect subPath={'action-flows'} />
                            )}
                          />
                          <PrivateRoute
                            path="/:capturedOrganisationId/*"
                            render={({capturedOrganisationId}) => (
                              <OrganisationProviderRoute
                                id={capturedOrganisationId}
                                organisations={organisations}
                                setOrganisationData={setOrganisationData}
                                authState={authState}
                                signOut={signOut}
                              />
                            )}
                          />
                          <Route
                            path="/demo-login/:appType"
                            render={({appType}) => {
                              if (appType === 'shell' || appType === 'demo') {
                                return <DemoApp appType={appType} />
                              }

                              return <NotFound />
                            }}
                          />
                          <Route
                            path="/signup/confirm"
                            render={() => <ConfirmAccount />}
                          />
                          <Route
                            path="/auth"
                            render={() => <HandleAuthCode />}
                          />
                          <Route
                            path="/login/:clientId"
                            render={({clientId}) => (
                              <HandleSSOLogin clientId={clientId} />
                            )}
                          />
                          <Route
                            path="/signup/:secretValue/*"
                            render={({secretValue}) => (
                              <AcceptInvitation secretValue={secretValue} />
                            )}
                          />
                          {authState.authenticated && (
                            <Route
                              path="/mfa-setup/*"
                              render={() => (
                                <MfaSetup
                                  authState={authState}
                                  auth={auth}
                                  checkPassword={false}
                                  onComplete={async redirect => {
                                    await auth.refreshAuthState()
                                    navigate(redirect ?? '/')
                                    location.reload()
                                  }}
                                  cancelLink={
                                    new URLSearchParams(
                                      document.location.search
                                    ).get('cancel') ?? '/'
                                  }
                                />
                              )}
                            />
                          )}
                          <Route path="*" render={() => <NotFound />} />
                        </Routes>
                      )}
                    </ToastProvider>
                  </ErrorBoundary>
                </Layout>
              )}
            />
            {auth.getAuthState().authenticated && inactivityTimeout ? (
              <SessionTimeout signOut={signOut} timeout={inactivityTimeout} />
            ) : null}
          </AtomicSDKProvider>
        </CrumbProvider>
      </KBarProviderWithFrecency>
    </FrecencyProvider>
  )
}

// returns value in seconds
export const getInactivityTimeout = (
  organisationId: string,
  organisationPreferences?: OrganisationPreferences
): number | undefined => {
  // allow testing via url param
  const override = new URLSearchParams(document.location.search).get(
    'inactivity_time'
  )
  if (override) {
    return parseInt(override)
  }

  // Use value in preferences if it exists
  if (organisationPreferences?.inactivityTimeout) {
    return Math.max(organisationPreferences.inactivityTimeout, 60) // Sane limit of 1 min
  }

  if (
    // all pre-prod
    ENVIRONMENT_NAME !== 'master' ||
    // plus atomic prod envs, excluding demo
    organisationId === '50-2' ||
    organisationId === '50-3'
  ) {
    return DEFAULT_INACTIVITY_TIMEOUT
  }

  if (organisationId.startsWith('67')) {
    return 600 // 10 min timeout for BNZ
  }

  return undefined
}
